import { gql, useLazyQuery } from "@apollo/client";
import { Card, CircularProgress, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";
import logo from "assets/images/logo.png";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import firmaGuia from "assets/images/firmaGuia.png";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "80%" },
  overflowY: "auto",
};
const meses = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export default function ModalGuiaPDF({ open, handleClose, idPermiso, selectedGuia }) {
  const [file, setFile] = useState(null);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [getPermiso, { data, loading }] = useLazyQuery(gql`
    query getPermiso($id: ID!) {
      permisoGob(id: $id) {
        id
        tipo
        fechaVencimiento
        estado
        anioPermiso
        nroPermiso
        fechaInicio
        url
        acompaniante
        usuario {
          nombre
          apellido
          username
          dni
          email
        }
        comprobantesPago {
          id
          nombre
          file
          estado
          tipo
        }
      }
    }
  `);

  const generatePDF = async () => {
    setImprimiendo(true);
    const element = document.getElementById("certificado");
    const contenidoPrincipal = element.querySelector("#contenido-principal");
    const firmaFinal = element.querySelector("#firma-final");
    const disposiciones = element.querySelector("#disposiciones");
    const firmaPagina2 = element.querySelector("#firma-pagina-2-container"); // Nuevo contenedor para la firma de la página 2

    // 1. Capturar la primera página (contenido principal y firma final)
    if (disposiciones) disposiciones.style.display = "none";
    if (firmaPagina2) firmaPagina2.style.display = "none"; // Asegúrate de que no se vea al capturar la primera página
    if (contenidoPrincipal) contenidoPrincipal.style.display = "";
    if (firmaFinal) firmaFinal.style.display = "";

    const canvas1 = await html2canvas(element, { scale: 2 });

    // 2. Capturar el contenido de las disposiciones (segunda página, sin la firma 2 por ahora)
    if (contenidoPrincipal) contenidoPrincipal.style.display = "none";
    if (firmaFinal) firmaFinal.style.display = "none";
    if (disposiciones) disposiciones.style.display = "";
    if (firmaPagina2) firmaPagina2.style.display = "none"; // Asegúrate de que no se vea al capturar las disposiciones

    const canvas2Disposiciones = await html2canvas(disposiciones, { scale: 2 });

    // 3. Capturar solo la sección de firma-pagina-2
    if (contenidoPrincipal) contenidoPrincipal.style.display = "none";
    if (firmaFinal) firmaFinal.style.display = "none";
    if (disposiciones) disposiciones.style.display = "none";
    if (firmaPagina2) firmaPagina2.style.display = ""; // Muestra solo este elemento para capturarlo

    const canvasFirmaPagina2 = await html2canvas(firmaPagina2, { scale: 2 });

    // Restaurar los estilos originales del DOM
    if (contenidoPrincipal) contenidoPrincipal.style.display = "";
    if (firmaFinal) firmaFinal.style.display = "";
    if (disposiciones) disposiciones.style.display = "";
    if (firmaPagina2) firmaPagina2.style.display = "";

    const report = new jsPDF({ orientation: "portrait", compress: true });

    const addCanvasToPDF = (pdf, canvas, addPage = false) => {
      const imgData = canvas.toDataURL("image/jpeg", 1);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      const marginX = (pdfWidth - scaledWidth) / 2;
      const marginY = 0; // Top aligned for main content

      if (addPage) pdf.addPage();
      pdf.addImage(imgData, "JPEG", marginX, marginY, scaledWidth, scaledHeight);
    };

    // Agregar la primera página
    addCanvasToPDF(report, canvas1);

    // Agregar la segunda página con las disposiciones
    report.addPage();
    addCanvasToPDF(report, canvas2Disposiciones);

    // 4. Posicionar la firma-pagina-2 al pie de la segunda página
    const pdfWidth = report.internal.pageSize.getWidth();
    const pdfHeight = report.internal.pageSize.getHeight();

    const imgDataFirmaPagina2 = canvasFirmaPagina2.toDataURL("image/jpeg", 1);
    const imgWidthFirmaPagina2 = canvasFirmaPagina2.width;
    const imgHeightFirmaPagina2 = canvasFirmaPagina2.height;

    // Calcular escala para la firma de la página 2
    const ratioFirmaPagina2 = pdfWidth / imgWidthFirmaPagina2; // Ajustar al ancho completo si es necesario
    const scaledWidthFirmaPagina2 = imgWidthFirmaPagina2 * ratioFirmaPagina2;
    const scaledHeightFirmaPagina2 = imgHeightFirmaPagina2 * ratioFirmaPagina2;

    const bottomMargin = 10; // Margen desde el borde inferior de la página

    // Calcular la posición Y para la firma de la página 2 (al pie)
    const yPosFirmaPagina2 = pdfHeight - scaledHeightFirmaPagina2 - bottomMargin;
    const xPosFirmaPagina2 = (pdfWidth - scaledWidthFirmaPagina2) / 2; // Centrar horizontalmente

    report.addImage(
      imgDataFirmaPagina2,
      "JPEG",
      xPosFirmaPagina2,
      yPosFirmaPagina2,
      scaledWidthFirmaPagina2,
      scaledHeightFirmaPagina2
    );
    setImprimiendo(false);

    report.save(`Guía de tránsito${selectedGuia.anioGuia}-${selectedGuia.nroGuia}.pdf`);
  };

  useEffect(() => {
    if (open) {
      getPermiso({ variables: { id: idPermiso } });
    } else {
      setFile(null);
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" p={3} pb={0}>
              <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                <SoftBox display="flex" alignItems="center">
                  <Tooltip title="Cerrar" placement="top">
                    <Icon
                      fontSize="medium"
                      onClick={() => {
                        handleClose();
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox id="certificado" py={3} px={12}>
              <SoftBox>
                <div id="contenido-principal">
                  <Grid container>
                    <Grid item xs={6} pb={1}>
                      <SoftTypography variant="h6">Dirección de biodiversidad</SoftTypography>
                      <SoftTypography variant="h6">Área fauna</SoftTypography>
                    </Grid>
                    <Grid item xs={6} display="flex" justifyContent="flex-end" pb={1}>
                      <SoftBox
                        component="img"
                        src={logo}
                        alt="Gobierno de la Provincia de San Luis Logo"
                        width="150px"
                      ></SoftBox>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      textAlign="center"
                      sx={{
                        lineHeight: 1,
                      }}
                      pb={1}
                    >
                      <SoftTypography variant="caption" fontWeight="bold">
                        Casa de Gobierno Ecológico &quot;Terrazas del Portezuelo&quot; <br />
                      </SoftTypography>
                      <SoftTypography variant="caption">
                        Edificio Capital - Bloque 2 - 2° Piso. Autopista Serranías Puntana Km 783
                        (5700) San Luis <br />
                      </SoftTypography>
                      <SoftTypography variant="caption">
                        e-mail: ccsilvestre@gmail.com / me.pmedioambiente@gmail.com <br />
                      </SoftTypography>
                      <SoftTypography variant="caption">
                        Tel: (+54) 2664452000 - Int. 3372
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={8} container pb={8}>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          borderLeft: "1px solid black",
                          borderBlock: "1px solid black",
                        }}
                      >
                        <SoftTypography variant="h5" textAlign="center" fontWeight="bold">
                          GUÍA DE TRÁNSITO
                        </SoftTypography>
                        <SoftTypography variant="body2" textAlign="center">
                          PARA PRODUCTOR Y/O SUBPRODUCTOS DE LA FAUNA SILVESTRE
                        </SoftTypography>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      container
                      xs={4}
                      textAlign="center"
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                    >
                      <Grid
                        item
                        xs={6}
                        sx={{
                          border: "1px solid black",
                          height: "40px",
                        }}
                      >
                        <SoftTypography variant="caption" fontWeight="bold">
                          ORIGINAL
                        </SoftTypography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          borderBlock: "1px solid black",
                          borderRight: "1px solid black",
                          height: "40px",
                        }}
                      >
                        <SoftTypography variant="caption" fontWeight="bold">
                          REGISTRO
                        </SoftTypography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          borderBottom: "1px solid black",
                          borderLeft: "1px solid black",
                          borderRight: "1px solid black",
                          height: "80px",
                        }}
                        display="flex"
                        justifyContent="space-around"
                        flexDirection="column"
                      >
                        <SoftTypography variant="subtitle2" fontWeight="bold">
                          {`Nº ${selectedGuia?.anioGuia} - ${selectedGuia?.nroGuia}`}
                        </SoftTypography>
                        <SoftTypography variant="subtitle2" fontWeight="bold">
                          SERIE &quot;PBD&quot;
                        </SoftTypography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        container
                        sx={{
                          borderBottom: "1px solid black",
                          borderRight: "1px solid black",
                          height: "80px",
                        }}
                      >
                        <Grid
                          item
                          xs={6}
                          sx={{
                            borderBottom: "1px solid black",
                          }}
                        ></Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            borderBottom: "1px solid black",
                            borderLeft: "1px solid black",
                          }}
                        ></Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            borderBottom: "1px solid black",
                          }}
                        ></Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            borderBottom: "1px solid black",
                            borderLeft: "1px solid black",
                          }}
                        ></Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            borderBottom: "1px solid black",
                          }}
                        ></Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            borderBottom: "1px solid black",
                            borderLeft: "1px solid black",
                          }}
                        ></Grid>
                        <Grid item xs={6} sx={{}}></Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            borderLeft: "1px solid black",
                          }}
                        ></Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      pb={1}
                      sx={{
                        lineHeight: 1,
                      }}
                      display="flex"
                      flexDirection="column"
                    >
                      <SoftTypography variant="caption" fontWeight="bold" pb={1}>
                        LA AUTORIDAD QUE SUSCRIBE POR MEDIO DE LA PRESENTE AUTORIZA:{" "}
                      </SoftTypography>
                      <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        mb={1}
                        sx={{
                          borderBottom: "1px dashed black",
                          fontSize: 16,
                        }}
                      >
                        &nbsp; &nbsp;
                        <strong>
                          {`${
                            selectedGuia?.usuario
                              ? `${selectedGuia?.usuario?.apellido}, ${selectedGuia?.usuario?.nombre} DNI N°${selectedGuia?.usuario?.dni}`
                              : `${
                                  selectedGuia?.nombreSolicitante || "Usuario no especificado"
                                } DNI N°${selectedGuia?.dniSolicitante || "No especificado"}`
                          }`}{" "}
                        </strong>
                      </SoftTypography>
                      <SoftTypography variant="caption">
                        a transportar fuera de los límites de esta provincia, los productos de la
                        fauna silvestre que a continuación se detallan.
                      </SoftTypography>
                    </Grid>
                    <Grid
                      container
                      item
                      xs={12}
                      sx={{
                        border: "1px solid black",
                      }}
                    >
                      <Grid
                        item
                        xs={3}
                        textAlign="center"
                        sx={{
                          borderRight: "1px solid black",
                          borderBottom: "1px solid black",
                        }}
                      >
                        <SoftTypography variant="subtitle2">Cantidad</SoftTypography>
                      </Grid>
                      <Grid
                        item
                        xs={4}
                        textAlign="center"
                        sx={{
                          borderRight: "1px solid black",
                          borderBottom: "1px solid black",
                        }}
                      >
                        <SoftTypography variant="subtitle2">Especie</SoftTypography>
                      </Grid>
                      <Grid
                        item
                        xs={5}
                        textAlign="center"
                        sx={{
                          borderBottom: "1px solid black",
                        }}
                      >
                        <SoftTypography variant="subtitle2">Descripción</SoftTypography>
                      </Grid>
                      {selectedGuia?.detalles?.map((item, index) => (
                        <Grid container item xs={12} key={index}>
                          <Grid
                            item
                            xs={3}
                            textAlign="center"
                            sx={{
                              borderRight: "1px solid black",
                              borderBottom:
                                index === selectedGuia?.detalles?.length - 1
                                  ? "none"
                                  : "1px solid black",
                              lineHeight: 1,
                            }}
                          >
                            <SoftTypography variant="caption">{item?.cantidad}</SoftTypography>
                          </Grid>
                          <Grid
                            item
                            xs={4}
                            textAlign="left"
                            pl={2}
                            sx={{
                              borderRight: "1px solid black",
                              borderBottom:
                                index === selectedGuia?.detalles?.length - 1
                                  ? "none"
                                  : "1px solid black",
                              lineHeight: 1,
                            }}
                          >
                            <SoftTypography variant="caption">{item?.especie}</SoftTypography>
                          </Grid>
                          <Grid
                            item
                            xs={5}
                            textAlign="left"
                            pl={2}
                            sx={{
                              borderBottom: "1px solid black",
                              borderBottom:
                                index === selectedGuia?.detalles?.length - 1
                                  ? "none"
                                  : "1px solid black",
                              lineHeight: 1,
                            }}
                          >
                            <SoftTypography variant="caption">{item?.descripcion}</SoftTypography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      py={0.5}
                      sx={{
                        lineHeight: 1,
                      }}
                      display="flex"
                      flexDirection="row"
                      alignItems="flex-end"
                    >
                      <SoftTypography variant="caption">ORIGINARIOS DE &nbsp;</SoftTypography>
                      <div
                        style={{
                          borderBottom: "1px dashed black",
                          flex: 1,
                        }}
                      >
                        <SoftTypography variant="caption">
                          <strong>{selectedGuia?.originariosDe}</strong>.
                        </SoftTypography>
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      py={0.5}
                      sx={{
                        lineHeight: 1,
                      }}
                      display="flex"
                      flexDirection="row"
                      alignItems="flex-end"
                    >
                      <SoftTypography variant="caption">
                        CON DESTINO A LA FIRMA O PERSONA JURÍDICA &nbsp;
                      </SoftTypography>
                      <div
                        style={{
                          borderBottom: "1px dashed black",
                          flex: 1,
                        }}
                      >
                        <SoftTypography variant="caption">
                          <strong>{selectedGuia?.destino}</strong>.
                        </SoftTypography>
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      py={0.5}
                      sx={{
                        lineHeight: 1,
                      }}
                      display="flex"
                      flexDirection="row"
                      alignItems="flex-end"
                    >
                      <SoftTypography variant="caption">CON DOMICILIO EN &nbsp;</SoftTypography>
                      <div
                        style={{
                          borderBottom: "1px dashed black",
                          flex: 1,
                        }}
                      >
                        <SoftTypography variant="caption">
                          <strong>{selectedGuia?.domicilioDestino}</strong>.
                        </SoftTypography>
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      py={0.5}
                      sx={{
                        lineHeight: 1,
                      }}
                      display="flex"
                      flexDirection="row"
                      alignItems="flex-end"
                    >
                      <SoftTypography variant="caption">MEDIO DE TRANSPORTE &nbsp;</SoftTypography>
                      <div
                        style={{
                          borderBottom: "1px dashed black",
                          flex: 1,
                        }}
                      >
                        <SoftTypography variant="caption">
                          <strong>{selectedGuia?.medioTransporte}</strong>.
                        </SoftTypography>
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        lineHeight: 1,
                        border: "1px solid black",
                      }}
                      p={1.5}
                    >
                      <SoftTypography variant="caption" fontWeight="bold">
                        VALIDO POR{" "}
                        <strong
                          style={{
                            borderBottom: "1px dashed black",
                            padding: "0 40px",
                          }}
                        >
                          {selectedGuia?.tiempoValidez} horas
                        </strong>{" "}
                        A PARTIR DE LA FECHA DE EMISIÓN
                      </SoftTypography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      pb={1}
                      pt={4}
                      sx={{
                        lineHeight: 1,
                      }}
                    >
                      <SoftTypography variant="subtitle2">
                        Expedido en San Luis a los{" "}
                        {dayjs(selectedGuia?.fechaExpedicion).format("DD")} días del mes de{" "}
                        {meses[Number(dayjs(selectedGuia?.fechaExpedicion).format("MM")) - 1]} del
                        año {dayjs(selectedGuia?.fechaExpedicion).format("YYYY")}.
                      </SoftTypography>
                    </Grid>
                    {/* PIE DE PÁGINA */}
                    <Grid
                      container
                      id="firma-final"
                      sx={{
                        position: imprimiendo ? "absolute" : "relative",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        width: "100%",
                        justifyContent: "space-between",
                        p: 2,
                      }}
                    >
                      <Grid item xs={7} />
                      <Grid
                        item
                        xs={5}
                        pb={1}
                        mt={15}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                      >
                        <img
                          src={firmaGuia}
                          alt="Firma Funcionario"
                          style={{ width: "100px", height: "auto" }}
                        />
                        <SoftTypography variant="caption" textAlign="center">
                          Carla Ocaña Leroy <br /> Jefa de Área Fauna <br /> Dirección Biodiversidad{" "}
                          <br />
                          Secretaria de Ambiente Y Desarrollo Sustentable
                        </SoftTypography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        pb={1}
                        sx={{
                          lineHeight: 1,
                        }}
                      >
                        <SoftTypography variant="caption">
                          Nota: Este documento es válido para el traslado de productos fuera de la
                          provincia de San Luis
                        </SoftTypography>
                      </Grid>
                    </Grid>
                  </Grid>
                </div>
                {/* SALTO DE PÁGINA */}
                <Grid container id="disposiciones" p={2}>
                  <Grid item xs={6} pb={1}></Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end" pb={1}>
                    <SoftBox
                      component="img"
                      src={logo}
                      alt="Gobierno de la Provincia de San Luis Logo"
                      width="150px"
                    ></SoftBox>
                  </Grid>
                  <Grid container p={2}>
                    <Grid
                      item
                      xs={12}
                      pb={1}
                      sx={{
                        lineHeight: 1,
                      }}
                    >
                      <Grid display="flex" justifyContent="center">
                        <SoftTypography variant="caption" fontWeight="bold">
                          DISPOSICIONES
                        </SoftTypography>
                      </Grid>
                      <SoftTypography
                        variant="caption"
                        sx={{
                          fontSize: 12,
                        }}
                      >
                        1° El presente documento sólo es válido a los efectos del transporte fuera
                        de los límites de esta provincia.
                        <br />
                        2° Cuando el producto y/o subproducto es transportado en vehículo automotor
                        específico, el responsable del todado debe presentar esta Guía en el primer
                        puesto policial al ingresar a la provincia de destino, a los efectos de que
                        el mismo sea inspeccionado por la Autoridad Policial y proceda a dejar
                        constancia de este acto (Certificación Policial).
                        <br /> 3° Si para el transporte del producto y/o subproducto fuera utilizado
                        el medio específico a que se hace mención en el punto SEGUNDO, el
                        destinatario del mismo debera presentar este documento dentro de los cinco
                        (5) días de su emisión ante el Organismo competente de su jurisdicción, a
                        los efectos de que este proceda a su inscripción y otorgue el Certificado de
                        Origen y Legítima Tenencia.
                        <br />
                        4° Si el producto y/o subproducto fuera transportado por un medio que no
                        fuera el citado anteriormente, sea este F.F C C. Debe observarse lo
                        siguiente. A) el remitente, una vez producido el despacho, presentará al
                        Organismo competente de su Jurisdicción el comprobante respectivo para que
                        efectúe las anotaciones del caso, y B) el destinatario, antes de proceder al
                        retiro del producto y/o subproducto del depósito de la empresa, hará conocer
                        esta situación al Organismo competente de su jurisdicción a los efectos que,
                        desde ese momento, quede sometido a las inspecciones que resulten necesarias
                        para extender el Certificado de Origen y Legítima Tenencia.
                        <br />
                        5° No se admitirán fotocopias de la presente Guía, sólo es válido el
                        original.
                        <br />
                        6° Se observará toda otra norma dictada al efecto.
                      </SoftTypography>
                    </Grid>
                  </Grid>
                </Grid>
                {/* Nuevo contenedor específico para la firma de la página 2 */}
                <Grid
                  container
                  id="firma-pagina-2-container" // Nuevo ID para el contenedor de la firma de la página 2
                  sx={{
                    position: imprimiendo ? "absolute" : "relative",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                    justifyContent: "space-between",
                    p: 2,
                  }}
                >
                  <Grid item xs={8} display="flex" justifyContent="flex-start" pb={1} mt={12}>
                    <SoftBox
                      component="img"
                      src={logo}
                      alt="Gobierno de la Provincia de San Luis Logo"
                      width="150px"
                    ></SoftBox>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    pb={1}
                    borderTop={"1px dashed black"}
                    textAlign="center"
                    mt={14}
                  >
                    <SoftTypography
                      variant="caption"
                      textAlign="center"
                      sx={{
                        fontSize: 12,
                      }}
                    >
                      NOTIFICADO
                    </SoftTypography>
                  </Grid>
                </Grid>
              </SoftBox>
            </SoftBox>
            <SoftBox p={3} pt={0}>
              <SoftButton color="primary" onClick={generatePDF} fullWidth>
                {loading ? ( // Usar el estado de carga correcto
                  <CircularProgress
                    size={15}
                    sx={{
                      marginRight: 0.5,
                    }}
                    color="white"
                  />
                ) : (
                  <Icon>download</Icon>
                )}
                &nbsp;Descargar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalGuiaPDF.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPermiso: PropTypes.string,
  selectedGuia: PropTypes.object,
};
